// The GroupFaces web app, recreated at 2x density for the camera: same surfaces, labels and type as the product
// (graphite dark, Inter + JetBrains Mono, one accent). Each screen is a fixed-size plane; its animation runs on the
// shot's local clock.
import React from 'react';
import {Img, staticFile} from 'remotion';
import {C, F, ease, easeOut, lerp} from './theme';
import {GALLERIES, IDENTIFIED, PEOPLE, REFS, TOTAL_PHOTOS, byFace, galleryPhotos, photosOf, PHOTOS, useT, type Person} from './data';
import {useS} from './i18n';
import {Avatar, Icon, PhotoTile} from './motion/Bits';
import {clamp01, ramp} from './motion/util';

const WIN = '#0d0e10';
const abs = (x: number, y: number, w?: number, h?: number): React.CSSProperties => ({position: 'absolute', left: x, top: y, width: w, height: h});
const typed = (s: string, k: number) => s.slice(0, Math.round(clamp01(k) * s.length));

// The app window: hairline frame as the outer background, content in an inner block, soft top-left sheen.
export const Window: React.FC<{W: number; H: number; children: React.ReactNode}> = ({W, H, children}) => (
 <div style={{...abs(0, 0, W, H), borderRadius: 40, background: 'rgba(255,255,255,.12)', boxShadow: '0 120px 220px rgba(0,0,0,.85)'}}>
  <div style={{position: 'absolute', inset: 2, borderRadius: 38, background: WIN, overflow: 'hidden', fontFamily: F.sans, color: C.ink}}>
   {children}
   <div style={{position: 'absolute', inset: 0, background: 'linear-gradient(128deg, rgba(255,255,255,.045), rgba(255,255,255,0) 38%)', pointerEvents: 'none'}}/>
  </div>
 </div>
);

const Card: React.FC<{x: number; y: number; w: number; h: number; r?: number; hi?: number; children?: React.ReactNode; style?: React.CSSProperties}> = ({x, y, w, h, r = 24, hi = 0, children, style}) => (
 <div style={{...abs(x, y, w, h), borderRadius: r, padding: 2, boxSizing: 'border-box', background: hi > 0 ? `rgba(113,112,255,${(0.14 + 0.6 * hi).toFixed(3)})` : C.line,
  boxShadow: hi > 0 ? `0 0 0 ${(10 * hi).toFixed(1)}px rgba(113,112,255,${(0.1 * hi).toFixed(3)})` : undefined, ...style}}>
  <div style={{width: '100%', height: '100%', borderRadius: r - 2, background: C.card, position: 'relative', overflow: 'hidden'}}>{children}</div>
 </div>
);

const Tag: React.FC<{color: string; children: React.ReactNode; size?: number; style?: React.CSSProperties}> = ({color, children, size = 24, style}) => (
 <div style={{display: 'inline-flex', alignItems: 'center', gap: 10, padding: `${size * 0.3}px ${size * 0.6}px`, borderRadius: size * 0.45, fontSize: size, fontWeight: 600,
  color, background: `${color}22`, border: `2px solid ${color}55`, whiteSpace: 'nowrap', ...style}}>{children}</div>
);

// ── Sidebar (shot 1) ─────────────────────────────────────────────────────────────────────────────────────────
export const SIDEBAR = {W: 1700, H: 1150, projetosY: 352};
const NAV_ICONS = ['grid', 'folder', 'coins', 'card', 'bars', 'help'];
const NAV_PROJECTS = 1, NAV_TOKENS = 2;   // the rows that carry a "+"
export const Sidebar: React.FC<{lightAt: number}> = ({lightAt}) => {
 const t = useT(), s = useS();
 const on = ramp(t, lightAt, 0.45, easeOut);
 return <Window W={SIDEBAR.W} H={SIDEBAR.H}>
  <div style={{...abs(0, 0, 500, SIDEBAR.H), background: '#0a0b0c', borderRight: `2px solid ${C.line}`}}>
   <Img src={staticFile('brand/logo.svg')} style={{...abs(48, 52, 52 * 303 / 55, 52)}}/>
   <div style={{...abs(36, 150, 428, 76), borderRadius: 18, border: `2px solid ${C.lineHi}`, display: 'flex', alignItems: 'center', gap: 16, padding: '0 22px', boxSizing: 'border-box'}}>
    <Icon name="search" size={30} color={C.muted}/><span style={{fontSize: 28, color: C.muted, flex: 1}}>Search...</span>
    <span style={{fontFamily: F.mono, fontSize: 18, color: C.muted, border: `2px solid ${C.lineHi}`, borderRadius: 8, padding: '3px 8px'}}>CTRL K</span>
   </div>
   {s.nav.map((label, i) => {
    const icon = NAV_ICONS[i], y = 270 + i * 82, hot = i === NAV_PROJECTS ? on : 0;
    return <div key={label} style={{...abs(28, y, 444, 68), borderRadius: 16, display: 'flex', alignItems: 'center', gap: 20, padding: '0 22px', boxSizing: 'border-box',
     background: `rgba(113,112,255,${(0.16 * hot).toFixed(3)})`, boxShadow: hot > 0 ? `inset 0 0 0 2px rgba(113,112,255,${(0.35 * hot).toFixed(3)})` : undefined}}>
     <Icon name={icon} size={32} color={hot > 0.5 ? C.accentHi : C.muted}/>
     <span style={{fontSize: 31, fontWeight: 500, color: hot > 0.5 ? C.ink : C.ink2, flex: 1}}>{label}</span>
     {(i === NAV_PROJECTS || i === NAV_TOKENS) && <Icon name="plus" size={28} color={C.muted}/>}
    </div>;
   })}
   <div style={{...abs(0, SIDEBAR.H - 120, 500, 2), background: C.line}}/>
   <div style={{...abs(44, SIDEBAR.H - 88, 420, 56), display: 'flex', alignItems: 'center', gap: 18}}>
    <div style={{width: 48, height: 48, borderRadius: 12, background: C.cardHi, fontFamily: F.mono, fontSize: 18, color: C.ink2, display: 'flex', alignItems: 'center', justifyContent: 'center'}}>EL</div>
    <span style={{fontSize: 29, fontWeight: 500, flex: 1}}>{s.studio}</span><Icon name="chevrons" size={28} color={C.muted}/>
   </div>
  </div>
  {/* the dashboard behind, out of focus */}
  <div style={{...abs(560, 52), fontSize: 34, fontWeight: 600}}>{s.dashboard}</div>
  {[['folder', s.dashCards[0], '3'], ['image', s.dashCards[1], s.num(TOTAL_PHOTOS)], ['mail', s.dashCards[2], '164']].map(([icon, l, v], i) =>
   <Card key={l} x={560 + i * 380} y={150} w={350} h={190}>
    <div style={{padding: 30}}><Icon name={icon} size={32} color={C.muted}/>
     <div style={{fontFamily: F.mono, fontSize: 18, letterSpacing: '.12em', color: C.muted, marginTop: 20}}>{l}</div>
     <div style={{fontFamily: F.mono, fontSize: 40, fontWeight: 600, marginTop: 6}}>{v}</div></div>
   </Card>)}
  <Card x={560} y={380} w={1100} h={520}>
   <div style={{padding: '30px 34px', fontSize: 28, fontWeight: 600}}>{s.recent}</div>
   {s.recentList.map((p, i) =>
    <div key={p} style={{display: 'flex', alignItems: 'center', padding: '28px 34px', borderTop: `2px solid ${C.line}`, fontSize: 26, color: C.ink2}}>
     <span style={{flex: 1}}>{p}</span><Tag color={C.green} size={20}>{s.done}</Tag></div>)}
  </Card>
 </Window>;
};

// ── Wizard stepper ───────────────────────────────────────────────────────────────────────────────────────────
const Stepper: React.FC<{active: number; x: number; y: number}> = ({active, x, y}) => (
 <div style={{...abs(x, y), display: 'flex', alignItems: 'center', gap: 22, fontSize: 26, whiteSpace: 'nowrap'}}>
  {useS().steps.map((s, i) => <React.Fragment key={s}>
   {i > 0 && <div style={{width: 60, height: 2, background: C.lineHi}}/>}
   <div style={{display: 'flex', alignItems: 'center', gap: 12, color: i === active ? C.ink : i < active ? C.ink2 : C.muted}}>
    <div style={{width: 40, height: 40, borderRadius: 20, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: F.mono, fontSize: 20,
     background: i === active ? C.accent : 'transparent', border: i === active ? 'none' : `2px solid ${C.lineHi}`, color: i === active ? '#fff' : C.muted}}>
     {i < active ? <Icon name="check" size={22} color={C.ink2}/> : i + 1}</div>{s}
   </div>
  </React.Fragment>)}
 </div>
);

// ── Targets (shot 2): reference photos drop in, one face each, the name fills from the file name ─────────────
export const TARGETS = {W: 1840, H: 1460, cardW: 820, cardH: 300, gx: 60, gy: 360, gap: 40};
export const targetXY = (i: number) => ({x: TARGETS.gx + (i % 2) * (TARGETS.cardW + TARGETS.gap), y: TARGETS.gy + Math.floor(i / 2) * (TARGETS.cardH + TARGETS.gap)});
const Field: React.FC<{x: number; y: number; w: number; icon: string; text: string; ph: string; caret?: boolean}> = ({x, y, w, icon, text, ph, caret}) => (
 <div style={{...abs(x, y, w, 64), borderRadius: 14, border: `2px solid ${caret ? 'rgba(113,112,255,.7)' : C.lineHi}`, background: C.bgMuted, display: 'flex', alignItems: 'center', gap: 14, padding: '0 18px', boxSizing: 'border-box'}}>
  <Icon name={icon} size={26} color={C.muted}/>
  <span style={{fontSize: 27, color: text ? C.ink : 'rgba(138,143,152,.55)', whiteSpace: 'nowrap'}}>{text || ph}</span>
  {caret && <div style={{width: 2, height: 32, background: C.accentHi}}/>}
 </div>
);
const TargetCard: React.FC<{p: Person; i: number; at: number}> = ({p, i, at}) => {
 const t = useT() - at, s = useS();
 if (t < 0) return null;
 const {x, y} = targetXY(i), ref = REFS[p.id];
 const drop = easeOut(t / 0.55), scan = clamp01((t - 0.35) / 0.55), badge = easeOut((t - 0.85) / 0.35), name = clamp01((t - 0.95) / 0.7);
 const R = 96;
 return <div style={{...abs(x, y - (1 - drop) * 70, TARGETS.cardW, TARGETS.cardH), opacity: drop, transform: `scale(${lerp(1.04, 1, drop).toFixed(4)})`}}>
  <Card x={0} y={0} w={TARGETS.cardW} h={TARGETS.cardH}>
   <div style={{...abs(40, 34)}}><Avatar src={ref.src} face={ref.face} iw={ref.w} ih={ref.h} size={R * 2}/></div>
   {/* the detector's sweep around the face, then a quiet green ring */}
   <svg style={{...abs(40 - 10, 34 - 10), overflow: 'visible'}} width={R * 2 + 20} height={R * 2 + 20}>
    <circle cx={R + 10} cy={R + 10} r={R + 6} fill="none" stroke={badge > 0.5 ? C.green : C.accentHi} strokeWidth={4} strokeLinecap="round"
     strokeDasharray={2 * Math.PI * (R + 6)} strokeDashoffset={2 * Math.PI * (R + 6) * (1 - scan)} transform={`rotate(-90 ${R + 10} ${R + 10})`}
     style={{filter: `drop-shadow(0 0 8px ${badge > 0.5 ? C.green : C.accent})`}} opacity={0.35 + 0.65 * (1 - badge * 0.5)}/>
   </svg>
   <div style={{...abs(40, 238, R * 2), display: 'flex', justifyContent: 'center', opacity: badge, transform: `scale(${lerp(0.7, 1, badge).toFixed(3)})`}}>
    <Tag color={C.green} size={21}><Icon name="user" size={20} color={C.green}/>{s.oneFace}</Tag>
   </div>
   <div style={{...abs(290, 40), fontFamily: F.mono, fontSize: 21, color: C.muted, letterSpacing: '.02em'}}>{p.file}</div>
   <Field x={290} y={84} w={490} icon="user" text={typed(p.name, name)} ph={s.namePh} caret={name > 0 && name < 1}/>
   <Field x={290} y={166} w={490} icon="mail" text="" ph={s.emailPh}/>
   <div style={{...abs(TARGETS.cardW - 60, 36)}}><Icon name="x" size={26} color="rgba(235,87,87,.75)"/></div>
  </Card>
 </div>;
};
export const Targets: React.FC<{times: number[]}> = ({times}) => {
 const s = useS();
 return <Window W={TARGETS.W} H={TARGETS.H}>
  <Img src={staticFile('brand/logo.svg')} style={{...abs(60, 50, 36 * 303 / 55, 36), opacity: 0.8}}/>
  <Stepper active={2} x={330} y={46}/>
  <div style={{...abs(0, 130, TARGETS.W, 2), background: C.line}}/>
  <div style={{...abs(60, 190), fontSize: 46, fontWeight: 600, letterSpacing: '-.01em'}}>{s.targetsTitle}</div>
  <div style={{...abs(60, 260), fontSize: 28, color: C.muted}}>{s.targetsSub}</div>
  {PEOPLE.map((p, i) => <TargetCard key={p.id} p={p} i={i} at={times[i]}/>)}
  {(() => {const {x, y} = targetXY(5); return <div style={{...abs(x, y, TARGETS.cardW, TARGETS.cardH), borderRadius: 24, border: `2px dashed ${C.lineHi}`, boxSizing: 'border-box',
   display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 16, color: C.muted, fontSize: 27}}>
   <Icon name="folder" size={44} color={C.accentHi}/>{s.addMore}</div>;})()}
 </Window>;
};

// ── Galleries (shot 3): event folders become gallery cards, the photo count climbs ────────────────────────────
export const GAL = {W: 2700, H: 1180, cardW: 580, cardH: 720, gx: 90, gy: 330, gap: 46};
export const galX = (i: number) => GAL.gx + i * (GAL.cardW + GAL.gap);
const countUp = (t: number, at: number, d: number, n: number) => n * easeOut(clamp01((t - at) / d));
export const Galleries: React.FC<{times: number[]}> = ({times}) => {
 const t = useT(), s = useS();
 const total = GALLERIES.reduce((s, g, i) => s + countUp(t, times[i] + 0.3, 1.4, g.count), 0);
 return <Window W={GAL.W} H={GAL.H}>
  <Stepper active={3} x={90} y={46}/>
  <div style={{...abs(0, 130, GAL.W, 2), background: C.line}}/>
  <div style={{...abs(90, 180), fontSize: 46, fontWeight: 600}}>{s.eventTitle}</div>
  <div style={{...abs(90, 250), fontSize: 28, color: C.muted}}>{s.eventSub}</div>
  <div style={{...abs(GAL.W - 90 - 760, 186, 760), display: 'flex', justifyContent: 'flex-end', alignItems: 'baseline', gap: 18, whiteSpace: 'nowrap'}}>
   <span style={{fontSize: 30, color: C.ink2}}>{s.galleriesN(GALLERIES.filter((_, i) => t > times[i] + 0.3).length)}</span>
   <span style={{fontFamily: F.mono, fontSize: 60, fontWeight: 600, color: C.ink, letterSpacing: '-.02em'}}>{s.num(total)}</span>
   <span style={{fontSize: 30, color: C.ink2}}>{s.photos}</span>
  </div>
  {GALLERIES.map((g, i) => {
   const k = ease(clamp01((t - times[i]) / 0.7)), x = galX(i);
   const gt = s.galleries[g.cat], ps = galleryPhotos(g.cat), mw = GAL.cardW - 44, tile = (mw - 12) / 2;
   return <Card key={g.cat} x={x} y={GAL.gy} w={GAL.cardW} h={GAL.cardH} hi={Math.sin(Math.PI * k) * 0.8}>
    {/* folder state */}
    <div style={{position: 'absolute', inset: 0, opacity: 1 - k, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 26}}>
     <Icon name="folder" size={120} color={C.accentHi} stroke={1.2}/>
     <div style={{fontFamily: F.mono, fontSize: 32, color: C.ink2}}>{gt.folder}</div>
    </div>
    {/* gallery state */}
    <div style={{position: 'absolute', inset: 0, opacity: k, transform: `scale(${lerp(0.96, 1, k).toFixed(4)})`}}>
     {ps.map((p, j) => <PhotoTile key={p.src} src={p.src} photo={p} w={tile} h={tile} r={14} style={{left: 22 + (j % 2) * (tile + 12), top: 22 + Math.floor(j / 2) * (tile + 12)}}/>)}
     <div style={{...abs(26, 22 + 2 * tile + 12 + 34), display: 'flex', alignItems: 'center', gap: 14}}>
      <Icon name="folder" size={30} color={C.accentHi}/><span style={{fontSize: 34, fontWeight: 600, whiteSpace: 'nowrap'}}>{gt.name}</span></div>
     <div style={{...abs(26, 22 + 2 * tile + 12 + 92), fontFamily: F.mono, fontSize: 24, color: C.muted, whiteSpace: 'nowrap'}}>
      {s.num(countUp(t, times[i] + 0.3, 1.4, g.count))} {s.files} · {gt.size}</div>
    </div>
   </Card>;
  })}
 </Window>;
};

// ── Project overview (shot 5): processing, then the metric cards count up ──────────────────────────────────────
export const OVER = {W: 2000, H: 1260};
export const metricXY = (i: number) => ({x: 70 + i * 630, y: 760});
const Tabs: React.FC<{active: number; y: number; counts?: Record<number, string>}> = ({active, y, counts = {}}) => (
 <div style={{...abs(70, y), display: 'flex', gap: 52, fontSize: 29, whiteSpace: 'nowrap'}}>
  {useS().tabs.map((s, i) => <div key={s} style={{paddingBottom: 18, color: i === active ? C.ink : C.muted, fontWeight: i === active ? 600 : 500, borderBottom: `3px solid ${i === active ? C.accent : 'transparent'}`, display: 'flex', gap: 12, alignItems: 'center'}}>
   {s}{counts[i] && <span style={{fontFamily: F.mono, fontSize: 20, color: C.muted, background: C.cardHi, borderRadius: 8, padding: '2px 10px'}}>{counts[i]}</span>}</div>)}
 </div>
);
const ProjectHead: React.FC<{done: number}> = ({done}) => {
 const s = useS();
 return <>
  <div style={{...abs(70, 54), display: 'flex', alignItems: 'center', gap: 14, fontSize: 26, color: C.muted}}><Icon name="back" size={26} color={C.muted}/>{s.back}</div>
  <div style={{...abs(70, 116), display: 'flex', alignItems: 'center', gap: 26}}>
   <span style={{fontSize: 50, fontWeight: 600, letterSpacing: '-.015em', whiteSpace: 'nowrap'}}>{s.project}</span>
   <div style={{position: 'relative'}}>
    <div style={{opacity: 1 - done}}><Tag color={C.accentHi} size={22}>{s.processing}</Tag></div>
    <div style={{position: 'absolute', left: 0, top: 0, opacity: done}}><Tag color={C.green} size={22}>{s.done}</Tag></div>
   </div>
  </div>
 </>;
};
export const Overview: React.FC<{doneAt: number}> = ({doneAt}) => {
 const t = useT(), s = useS();
 const prog = clamp01(ease(t / doneAt) * 1.0), done = ramp(t, doneAt, 0.4, easeOut);
 const thumbs = byFace(PHOTOS).filter((_, i) => i % 3 === 0).slice(0, 9);
 const count = (n: number, i: number) => s.num(n * easeOut(clamp01((t - doneAt - 0.25 - i * 0.25) / 1.6)));
 const metrics: [string, string, string][] = [['image', s.metrics[0], count(TOTAL_PHOTOS, 0)], ['folder', s.metrics[1], count(GALLERIES.length, 1)], ['users', s.metrics[2], count(IDENTIFIED, 2)]];
 return <Window W={OVER.W} H={OVER.H}>
  <ProjectHead done={done}/>
  <Tabs active={0} y={214}/>
  <div style={{...abs(0, 268, OVER.W, 2), background: C.line}}/>
  <Card x={70} y={320} w={OVER.W - 140} h={390}>
   <div style={{...abs(40, 36), display: 'flex', alignItems: 'center', gap: 16, fontSize: 32, fontWeight: 600}}>
    <Icon name={done > 0.5 ? 'check' : 'scan'} size={34} color={done > 0.5 ? C.green : C.accentHi}/>{done > 0.5 ? s.recognitionDone : s.processingPhotos}
    <span style={{fontFamily: F.mono, fontSize: 28, color: done > 0.5 ? C.green : C.accentHi, marginLeft: 6}}>{Math.round(prog * 100)}%</span>
   </div>
   <div style={{...abs(OVER.W - 140 - 40 - 520, 42, 520), textAlign: 'right', fontFamily: F.mono, fontSize: 24, color: C.muted}}>{s.num(TOTAL_PHOTOS * prog)} / {s.num(TOTAL_PHOTOS)} {s.photos}</div>
   <div style={{...abs(40, 110, OVER.W - 220, 10), borderRadius: 5, background: C.cardHi}}>
    <div style={{width: `${prog * 100}%`, height: '100%', borderRadius: 5, background: done > 0.5 ? C.green : C.accent, boxShadow: `0 0 18px ${done > 0.5 ? C.green : C.accent}`}}/>
   </div>
   {thumbs.map((p, i) => {
    const lit = clamp01((prog - i / thumbs.length) * thumbs.length);
    return <PhotoTile key={p.src} src={p.src} photo={p} w={180} h={180} r={14} dim={0.65 * (1 - lit)} ring={lit > 0 && lit < 1 ? 0.9 : 0} style={{left: 40 + i * 198, top: 170}}/>;
   })}
  </Card>
  {metrics.map(([icon, label, value], i) => {
   const {x, y} = metricXY(i), hot = i === 2 ? ramp(t, doneAt + 0.8, 0.8, easeOut) : 0;
   return <Card key={label} x={x} y={y} w={600} h={300} hi={hot}>
    <div style={{padding: 44}}>
     <div style={{width: 80, height: 80, borderRadius: 18, background: i === 2 ? 'rgba(113,112,255,.16)' : C.bgMuted, display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
      <Icon name={icon} size={40} color={i === 2 ? C.accentHi : C.muted}/></div>
     <div style={{fontSize: 30, color: C.muted, marginTop: 30}}>{label}</div>
     <div style={{fontFamily: F.mono, fontSize: 70, fontWeight: 600, marginTop: 6, letterSpacing: '-.02em'}}>{value}</div>
    </div>
   </Card>;
  })}
 </Window>;
};

// ── Clientes tab (shot 6): one gallery per student, faces in every thumbnail ───────────────────────────────────
export const CLI = {W: 2760, H: 1630, rowY: 330, rowH: 230, gap: 24};
export const cliRowY = (i: number) => CLI.rowY + i * (CLI.rowH + CLI.gap);
export const Clients: React.FC = () => {
 const s = useS();
 return <Window W={CLI.W} H={CLI.H}>
  <ProjectHead done={1}/>
  <Tabs active={2} y={214} counts={{1: '4', 2: String(IDENTIFIED)}}/>
  <div style={{...abs(0, 268, CLI.W, 2), background: C.line}}/>
  {PEOPLE.map((p, i) => {
   const y = cliRowY(i), ref = REFS[p.id], ps = photosOf(p.id).slice(0, 8);
   return <Card key={p.id} x={70} y={y} w={CLI.W - 140} h={CLI.rowH}>
    <div style={{...abs(36, 45)}}><Avatar src={ref.src} face={ref.face} iw={ref.w} ih={ref.h} size={140} ring/></div>
    <div style={{...abs(210, 62), fontSize: 36, fontWeight: 600, whiteSpace: 'nowrap'}}>{p.name}</div>
    <div style={{...abs(210, 116), fontFamily: F.mono, fontSize: 22, color: C.muted}}>{p.email}</div>
    <div style={{...abs(700, 86)}}><Tag color={C.accentHi} size={24}><Icon name="image" size={24} color={C.accentHi}/>{p.photos} {s.photos}</Tag></div>
    {ps.map((ph, j) => <PhotoTile key={ph.src} src={ph.src} photo={ph} w={190} h={190} r={14} style={{left: 960 + j * 206, top: 20}}/>)}
   </Card>;
  })}
 </Window>;
};

// ── Send button (shot 7): macro on the control, cursor press, the button pops ─────────────────────────────────
export const SEND = {W: 1500, H: 820, bx: 1000, by: 610, bw: 470, bh: 116};
const SELECTED_PHOTOS = PEOPLE.reduce((n, p) => n + p.photos, 0);
const Cursor: React.FC<{x: number; y: number; s?: number}> = ({x, y, s = 1}) => (
 <svg style={{...abs(x, y), overflow: 'visible', transform: `scale(${s})`, transformOrigin: '0 0', filter: 'drop-shadow(0 6px 10px rgba(0,0,0,.6))'}} width={56} height={72} viewBox="0 0 28 36">
  <path d="M2 2v27l7-7 5 11 5-2-5-11h10z" fill="#fff" stroke="#111" strokeWidth={1.6} strokeLinejoin="round"/>
 </svg>
);
export const Send: React.FC<{pressAt: number}> = ({pressAt}) => {
 const t = useT(), s = useS();
 const move = ease((t - (pressAt - 1.3)) / 1.15);
 const down = ramp(t, pressAt, 0.12, easeOut) * (1 - ramp(t, pressAt + 0.14, 0.3, easeOut));
 const pop = ramp(t, pressAt + 0.14, 0.9, easeOut), sent = ramp(t, pressAt + 0.9, 0.4, easeOut);
 const {bx, by, bw, bh} = SEND;
 return <Window W={SEND.W} H={SEND.H}>
  <div style={{...abs(70, 64), display: 'flex', alignItems: 'center', gap: 18, fontSize: 40, fontWeight: 600}}><Icon name="mail" size={40} color={C.accentHi}/>{s.sendTitle}</div>
  <div style={{...abs(70, 132), fontSize: 28, color: C.muted}}>{s.sendSub}</div>
  <div style={{...abs(70, 230), display: 'flex', alignItems: 'center'}}>
   {PEOPLE.map((p, i) => <div key={p.id} style={{marginLeft: i ? -26 : 0, borderRadius: '50%', boxShadow: `0 0 0 6px ${C.card}`}}>
    <Avatar src={REFS[p.id].src} face={REFS[p.id].face} iw={REFS[p.id].w} ih={REFS[p.id].h} size={100}/></div>)}
   <span style={{fontSize: 30, color: C.ink2, marginLeft: 30}}>{s.selected(PEOPLE.length, s.num(SELECTED_PHOTOS))}</span>
  </div>
  <div style={{...abs(70, 400, 1360, 2), background: C.line}}/>
  <div style={{...abs(70, 470), fontSize: 27, color: C.muted, width: 800, lineHeight: 1.45}}>{s.sendNote}</div>
  {/* pop: a ring expanding from the button */}
  {pop > 0 && pop < 1 && <div style={{...abs(bx - 24 * pop, by - 24 * pop, bw + 48 * pop, bh + 48 * pop), borderRadius: 26 + 24 * pop, border: `3px solid rgba(130,143,255,${(0.8 * (1 - pop)).toFixed(3)})`}}/>}
  <div style={{...abs(bx, by, bw, bh), borderRadius: 26, background: sent > 0.5 ? C.green : C.accent, transform: `scale(${(1 - 0.05 * down + 0.04 * Math.sin(Math.PI * pop)).toFixed(4)})`,
   boxShadow: `0 0 0 2px rgba(255,255,255,.12) inset, 0 20px 50px rgba(113,112,255,${(0.35 + 0.4 * Math.sin(Math.PI * pop)).toFixed(3)})`,
   display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 20, fontSize: 38, fontWeight: 600, color: '#fff', whiteSpace: 'nowrap'}}>
   <Icon name={sent > 0.5 ? 'check' : 'send'} size={38} color="#fff" stroke={2.2}/>{sent > 0.5 ? s.sent : s.send}
  </div>
  <Cursor x={lerp(bx + bw * 0.62 + 170, bx + bw * 0.62, move)} y={lerp(by + bh + 80, by + bh * 0.52, move)} s={1 - 0.08 * down}/>
 </Window>;
};

// ── Delivery status (shot 8): enviado → aberto → baixado, with date and time ──────────────────────────────────
export const DEL = {W: 1780, H: 1240, rowY: 300, rowH: 170, chipX: 860};
export const delRowY = (i: number) => DEL.rowY + i * DEL.rowH;
const STATES = [{icon: 'send', color: C.accentHi}, {icon: 'eye', color: '#4ea7fc'}, {icon: 'download', color: C.green}];
// per row: when each state is reached (shot seconds) and its timestamp; Mariana has not downloaded yet
const TIMES: [number, string][][] = [
 [[0.3, '14:02'], [1.3, '14:11'], [2.6, '14:15']],
 [[0.4, '14:02'], [1.8, '14:26'], [3.1, '14:31']],
 [[0.5, '14:02'], [2.2, '14:37'], [3.6, '14:44']],
 [[0.6, '14:02'], [2.7, '14:52'], [99, '']],
 [[0.7, '14:02'], [3.2, '15:08'], [4.3, '15:16']],
];
const Chip: React.FC<{state: number; at: number; time: string; x: number}> = ({state, at, time, x}) => {
 const t = useT(), L = useS();
 const k = ramp(t, at, 0.4, easeOut), s = STATES[state];
 return <div style={{...abs(x, 30, 250, 110), borderRadius: 20, boxSizing: 'border-box', padding: '16px 22px',
  border: `2px solid ${k > 0 ? s.color + '66' : C.lineHi}`, background: k > 0 ? `${s.color}${Math.round(0x1c * k).toString(16).padStart(2, '0')}` : 'transparent',
  transform: `scale(${(1 + 0.06 * Math.sin(Math.PI * k)).toFixed(4)})`, boxShadow: k > 0 && k < 1 ? `0 0 30px ${s.color}55` : undefined}}>
  <div style={{display: 'flex', alignItems: 'center', gap: 12, fontSize: 28, fontWeight: 600, color: k > 0 ? s.color : 'rgba(138,143,152,.6)'}}>
   <Icon name={s.icon} size={28} color={k > 0 ? s.color : 'rgba(138,143,152,.6)'}/>{L.states[state]}</div>
  <div style={{fontFamily: F.mono, fontSize: 22, color: k > 0 ? C.ink2 : 'rgba(138,143,152,.45)', marginTop: 8, opacity: k > 0 ? 1 : 0.8}}>{k > 0 ? L.stamp(time) : '—'}</div>
 </div>;
};
export const Delivery: React.FC = () => {
 const s = useS();
 return <Window W={DEL.W} H={DEL.H}>
  <ProjectHead done={1}/>
  <Tabs active={s.deliveryTab} y={214}/>
  <div style={{...abs(0, 268, DEL.W, 2), background: C.line}}/>
  {PEOPLE.map((p, i) => {
   const ref = REFS[p.id];
   return <div key={p.id} style={{...abs(70, delRowY(i), DEL.W - 140, DEL.rowH), borderBottom: `2px solid ${C.line}`}}>
    <div style={{...abs(10, 35)}}><Avatar src={ref.src} face={ref.face} iw={ref.w} ih={ref.h} size={100}/></div>
    <div style={{...abs(140, 44), fontSize: 33, fontWeight: 600, whiteSpace: 'nowrap'}}>{p.name}</div>
    <div style={{...abs(140, 94), fontFamily: F.mono, fontSize: 21, color: C.muted}}>{p.email}</div>
    {TIMES[i].map(([at, time], s) => <Chip key={s} state={s} at={at} time={time} x={DEL.chipX - 70 + s * 280}/>)}
   </div>;
  })}
 </Window>;
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
